/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { environment } from '@environments/environment';
import { map } from 'rxjs/operators';
import { OutputAdapterService } from './../output-adapter/output-adapter.service';
import { InputAdapterService } from './../input-adapter/input-adapter.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ApiParams,
  ChecklistDTO,
  SurveyUpdateDTO,
  PositionDTO,
  WizardDTO,
  SurveyDTO,
  FormDTO,
  DomainListDTO,
  IdentificationLinkDTO,
  FormUpdateDTO,
  LabelListDTO,
  UserDataDTO,
  WizardStepNextOutputDTO,
  ReportRequestDTO,
  AuthenticateDTO,
  IdentificationVerifyDTO,
  OcrCheckDTO,
  DigitalSigningDTO,
  QuotationDTO,
  CustomerDTO,
  QuotationParamsDTO,
  BankAccountLinkDTO,
  BankAccountEvaluationDTO,
  NoteDTO,
  UserCustomerDTO,
  UserCustomerUpdateDTO,
  AtcDTO,
  PositionSaveRequestDTO,
  PositionRequestDTO,
  NotificationDTO,
  CreditRequestDTO,
  QuotationBoundariesDTO,
  CreditUseListDTO,
  PostsellingCreateDTO,
  TaskListDTO,
  PositionCreateDTO,
  ReportDTO,
  HandoverStartDTO,
  HandoverStatusDTO,
  LogRequestDTO,
  ChannelDTO,
  OcrScanDTO,
  Api,
  ChecksDTO,
  CampaignDTO
} from '../../models';

@Injectable()
export class ApiService implements Api {
  protected baseUrl = environment.baseUrl;

  protected apiUrl = environment.apiBaseUrl;

  protected securityUrl = environment.securityBaseUrl;

  constructor(
    protected http: HttpClient,
    protected outputAdapter: OutputAdapterService,
    protected inputAdapter: InputAdapterService
  ) {}

  putLog(params: ApiParams<void, LogRequestDTO>): Observable<any> {
    const url = `${this.apiUrl}/v1/user/action`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putLog(params).body,
        this.inputAdapter.putLog(params).options
      )
      .pipe(map(res => this.outputAdapter.putLog(res)));
  }

  getLabels(params: ApiParams<void, void>): Observable<LabelListDTO> {
    const url = `${this.apiUrl}/v1/labels`;

    return this.http
      .get<LabelListDTO>(url, this.inputAdapter.getLabels(params).options)
      .pipe(map(res => this.outputAdapter.getLabels(res)));
  }

  getChecks(
    params: ApiParams<{ positionId: string; ndg: string }, void>
  ): Observable<ChecksDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/customers/${params.pathParams.ndg}/checks`;

    return this.http
      .get<ChecksDTO>(url, this.inputAdapter.getChecks(params).options)
      .pipe(map(res => this.outputAdapter.getChecks(res)));
  }

  getDomain(
    params: ApiParams<{ code: string }, void>
  ): Observable<DomainListDTO> {
    const url = `${this.apiUrl}/v1/domains/${params.pathParams.code}`;

    return this.http
      .get<DomainListDTO>(url, this.inputAdapter.getDomain(params).options)
      .pipe(map(res => this.outputAdapter.getDomain(res)));
  }

  postSecurityLogon(params: ApiParams<void, AuthenticateDTO>): Observable<any> {
    const url = `${this.securityUrl}/authenticate`;

    return this.http
      .post<void>(
        url,
        this.inputAdapter.postSecurityLogon(params).body,
        this.inputAdapter.postSecurityLogon(params).options
      )
      .pipe(map(res => this.outputAdapter.postSecurityLogon(res)));
  }

  getSecurityVerify(params: ApiParams<void, void>): Observable<void> {
    const url = `${this.securityUrl}/verify`;

    return this.http
      .get<void>(url, this.inputAdapter.getSecurityVerify(params).options)
      .pipe(map(res => this.outputAdapter.getSecurityVerify(res)));
  }

  getSecurityUserData(params: ApiParams<void, void>): Observable<UserDataDTO> {
    const url = `${this.securityUrl}/userdata`;

    return this.http
      .get<UserDataDTO>(
        url,
        this.inputAdapter.getSecurityUserData(params).options
      )
      .pipe(map(res => this.outputAdapter.getSecurityUserData(res)));
  }

  getOnboardingCustomer(
    params: ApiParams<{ userId: string }, void>
  ): Observable<UserCustomerDTO> {
    const url = `${this.apiUrl}/v1/onboarding/customers/${params.pathParams.userId}`;

    return this.http
      .get<UserCustomerDTO>(
        url,
        this.inputAdapter.getOnboardingCustomer(params).options
      )
      .pipe(map(res => this.outputAdapter.getOnboardingCustomer(res)));
  }

  putOnboardingCustomer(
    params: ApiParams<{ userId: string }, UserCustomerUpdateDTO>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/onboarding/customers/${params.pathParams.userId}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putOnboardingCustomer(params).body,
        this.inputAdapter.putOnboardingCustomer(params).options
      )
      .pipe(map(res => this.outputAdapter.putOnboardingCustomer(res)));
  }

  getQuotationsBoundaries(
    params: ApiParams<void, void>
  ): Observable<QuotationBoundariesDTO> {
    const url = `${this.apiUrl}/v1/quotations/simulator-boundaries`;

    return this.http
      .get<QuotationBoundariesDTO>(
        url,
        this.inputAdapter.getQuotationsBoundaries(params).options
      )
      .pipe(map(res => this.outputAdapter.getQuotationsBoundaries(res)));
  }

  postQuotations(
    params: ApiParams<void, QuotationParamsDTO>
  ): Observable<QuotationDTO> {
    const url = `${this.apiUrl}/v1/quotations`;

    return this.http
      .post<QuotationDTO>(
        url,
        this.inputAdapter.postQuotations(params).body,
        this.inputAdapter.postQuotations(params).options
      )
      .pipe(map(res => this.outputAdapter.postQuotations(res)));
  }

  postPositionSave(
    params: ApiParams<void, PositionSaveRequestDTO>
  ): Observable<string> {
    const url = `${this.apiUrl}/v1/positions/save`;

    return this.http
      .post<string>(
        url,
        this.inputAdapter.postPositionSave(params).body,
        this.inputAdapter.postPositionSave(params).options
      )
      .pipe(map(res => this.outputAdapter.postPositionSave(res)));
  }

  postPosition(
    params: ApiParams<void, PositionRequestDTO>
  ): Observable<PositionCreateDTO> {
    const url = `${this.apiUrl}/v1/position`;

    return this.http
      .post<PositionCreateDTO>(
        url,
        this.inputAdapter.postPosition(params).body,
        this.inputAdapter.postPosition(params).options
      )
      .pipe(map(res => this.outputAdapter.postPosition(res)));
  }

  putPosition(
    params: ApiParams<void, { positionId: string }>
  ): Observable<PositionCreateDTO> {
    const url = `${this.apiUrl}/v2/position`;

    return this.http
      .put<PositionCreateDTO>(
        url,
        this.inputAdapter.putPosition(params).body,
        this.inputAdapter.putPosition(params).options
      )
      .pipe(map(res => this.outputAdapter.putPosition(res)));
  }

  getPersonalPositions(
    params: ApiParams<void, void>
  ): Observable<PositionDTO[]> {
    const url = `${this.apiUrl}/v1/personal/positions`;

    return this.http
      .get<PositionDTO[]>(
        url,
        this.inputAdapter.getPersonalPositions(params).options
      )
      .pipe(map(res => this.outputAdapter.getPersonalPositions(res)));
  }

  getCampaign(
    params: ApiParams<{ taxNumber: string }, void>
  ): Observable<CampaignDTO> {
    const url = `${this.apiUrl}/v1/quotations/${params.pathParams.taxNumber}/campaign-details`;

    return this.http
      .get<CampaignDTO>(url, this.inputAdapter.getCampaign(params).options)
      .pipe(map(res => this.outputAdapter.getCampaign(res)));
  }

  getPersonalTasks(params: ApiParams<void, void>): Observable<TaskListDTO> {
    const url = `${this.apiUrl}/v1/personal/tasks`;

    return this.http
      .get<TaskListDTO>(url, this.inputAdapter.getPersonalTasks(params).options)
      .pipe(map(res => this.outputAdapter.getPersonalTasks(res)));
  }

  getPosition(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<PositionDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}`;

    return this.http
      .get<PositionDTO>(url, this.inputAdapter.getPosition(params).options)
      .pipe(map(res => this.outputAdapter.getPosition(res)));
  }

  getPositionQuotation(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<QuotationDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/quotation`;

    return this.http
      .get<PositionDTO>(
        url,
        this.inputAdapter.getPositionQuotation(params).options
      )
      .pipe(map(res => this.outputAdapter.getPositionQuotation(res)));
  }

  getPositionCustomer(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<CustomerDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/customer`;

    return this.http
      .get<PositionDTO>(
        url,
        this.inputAdapter.getPositionCustomer(params).options
      )
      .pipe(map(res => this.outputAdapter.getPositionCustomer(res)));
  }

  getPositionFull(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<PositionDTO> {
    const url = `${this.apiUrl}/v1/positions/full/${params.pathParams.positionId}`;

    return this.http
      .get<PositionDTO>(url, this.inputAdapter.getPositionFull(params).options)
      .pipe(map(res => this.outputAdapter.getPositionFull(res)));
  }

  postPositionWithdraw(
    params: ApiParams<{ positionId: string }, { reasonCode: string }>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/WITHDRAW_REQUEST`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postPositionWithdraw(params).body,
        this.inputAdapter.postPositionWithdraw(params).options
      )
      .pipe(map(res => this.outputAdapter.postPositionWithdraw(res)));
  }

  getPreWizard(
    params: ApiParams<{ wizardCode: string }, void>
  ): Observable<WizardDTO> {
    const url = `${this.apiUrl}/v1/wizards/${params.pathParams.wizardCode}`;

    return this.http
      .get<WizardDTO>(url, this.inputAdapter.getPreWizard(params).options)
      .pipe(map(res => this.outputAdapter.getPreWizard(res)));
  }

  getWizard(
    params: ApiParams<{ positionId: string; wizardCode: string }, void>
  ): Observable<WizardDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/wizards/${params.pathParams.wizardCode}`;

    return this.http
      .get<WizardDTO>(url, this.inputAdapter.getWizard(params).options)
      .pipe(map(res => this.outputAdapter.getWizard(res)));
  }

  putWizardStepNext(
    params: ApiParams<
      { positionId: string; wizardCode: string; stepCode: string },
      Record<string, never>
    >
  ): Observable<WizardStepNextOutputDTO> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/wizards/${params.pathParams.wizardCode}/steps/${params.pathParams.stepCode}/next`;

    return this.http
      .put<WizardStepNextOutputDTO>(
        url,
        this.inputAdapter.putWizardStepNext(params).body,
        this.inputAdapter.putWizardStepNext(params).options
      )
      .pipe(map(res => this.outputAdapter.putWizardStepNext(res)));
  }

  putWorkflowTaskClose(
    params: ApiParams<
      { positionId: string; stepCode: string },
      Record<string, never>
    >
  ): Observable<WizardStepNextOutputDTO> {
    const url = `${this.apiUrl}/v1/workflow/tasks/close/${params.pathParams.positionId}/step/${params.pathParams.stepCode}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putWorkflowTaskClose(params).body,
        this.inputAdapter.putWorkflowTaskClose(params).options
      )
      .pipe(map(res => this.outputAdapter.putWorkflowTaskClose(res)));
  }

  getSurvey(
    params: ApiParams<{ positionId: string; surveyCode: string }, void>
  ): Observable<SurveyDTO[]> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/surveys/${params.pathParams.surveyCode}`;

    return this.http
      .get<SurveyDTO[]>(url, this.inputAdapter.getSurvey(params).options)
      .pipe(map(res => this.outputAdapter.getSurvey(res)));
  }

  putSurvey(
    params: ApiParams<
      { positionId: string; surveyCode: string },
      SurveyUpdateDTO
    >
  ) {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/surveys/${params.pathParams.surveyCode}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putSurvey(params).body,
        this.inputAdapter.putSurvey(params).options
      )
      .pipe(map(res => this.outputAdapter.putSurvey(res)));
  }

  getForm(
    params: ApiParams<{ positionId: string; formCode: string }, void>
  ): Observable<FormDTO[]> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/forms/${params.pathParams.formCode}`;

    return this.http
      .get<FormDTO[]>(url, this.inputAdapter.getForm(params).options)
      .pipe(map(res => this.outputAdapter.getForm(res)));
  }

  postForm(
    params: ApiParams<{ positionId: string; formCode: string }, FormUpdateDTO>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/forms/${params.pathParams.formCode}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postForm(params).body,
        this.inputAdapter.postForm(params).options
      )
      .pipe(map(res => this.outputAdapter.postForm(res)));
  }

  getChecklist(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<ChecklistDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current`;

    return this.http
      .get<ChecklistDTO>(url, this.inputAdapter.getChecklist(params).options)
      .pipe(map(res => this.outputAdapter.getChecklist(res)));
  }

  getChecklistDocumentDownload(
    params: ApiParams<{ positionId: string; docId: string }, void>
  ): Observable<any> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/download`;

    return this.http
      .get(url, this.inputAdapter.getChecklistDocumentDownload(params).options)
      .pipe(map(res => this.outputAdapter.getChecklistDocumentDownload(res)));
  }

  postChecklistDocumentUpload(
    params: ApiParams<{ positionId: string; docId: string }, FormData>
  ): Observable<number> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/file`;

    return this.http
      .post<number>(
        url,
        this.inputAdapter.postChecklistDocumentUpload(params).body,
        this.inputAdapter.postChecklistDocumentUpload(params).options
      )
      .pipe(map(res => this.outputAdapter.postChecklistDocumentUpload(res)));
  }

  putChecklistDocumentOverride(
    params: ApiParams<
      { positionId: string; docId: string; fileId: string },
      FormData
    >
  ): Observable<number> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/file/${params.pathParams.fileId}`;

    return this.http
      .put<number>(
        url,
        this.inputAdapter.putChecklistDocumentOverride(params).body,
        this.inputAdapter.putChecklistDocumentOverride(params).options
      )
      .pipe(map(res => this.outputAdapter.putChecklistDocumentOverride(res)));
  }

  getChecklistDocumentScan(
    params: ApiParams<{ positionId: string; docId: string }, void>
  ): Observable<OcrScanDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/scan`;

    return this.http
      .get<OcrScanDTO>(
        url,
        this.inputAdapter.getChecklistDocumentScan(params).options
      )
      .pipe(map(res => this.outputAdapter.getChecklistDocumentScan(res)));
  }

  getChecklistDocumentCheckOcr(
    params: ApiParams<{ positionId: string; docId: string }, void>
  ): Observable<OcrCheckDTO> {
    // eslint-disable-next-line max-len
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/checklists/current/documents/${params.pathParams.docId}/check-ocr`;

    return this.http
      .get<OcrCheckDTO>(
        url,
        this.inputAdapter.getChecklistDocumentCheckOcr(params).options
      )
      .pipe(map(res => this.outputAdapter.getChecklistDocumentCheckOcr(res)));
  }

  getReports(
    params: ApiParams<{ printType: string }, void>
  ): Observable<ReportDTO[]> {
    const url = `${this.apiUrl}/v1/reports/${params.pathParams.printType}`;

    return this.http
      .get<ReportDTO[]>(url, this.inputAdapter.getReports(params).options)
      .pipe(map(res => this.outputAdapter.getReports(res)));
  }

  postReportModule(params: ApiParams<void, any>): Observable<any> {
    const url = `${this.apiUrl}/v1/reports/module`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postReportModule(params).body,
        this.inputAdapter.postReportModule(params).options
      )
      .pipe(map(res => this.outputAdapter.postReportModule(res)));
  }

  postReport(
    params: ApiParams<
      { positionId: string; reportCode: string },
      ReportRequestDTO
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/reports/${params.pathParams.reportCode}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postReport(params).body,
        this.inputAdapter.postReport(params).options
      )
      .pipe(map(res => this.outputAdapter.postReport(res)));
  }

  postIdentificationStart(
    params: ApiParams<
      { method: string },
      {
        businessId: string;
        ndg: string;
      }
    >
  ): Observable<IdentificationLinkDTO> {
    const url = `${this.apiUrl}/v1/identifications/${params.pathParams.method}/start`;

    return this.http
      .post<IdentificationLinkDTO>(
        url,
        this.inputAdapter.postIdentificationStart(params).body,
        this.inputAdapter.postIdentificationStart(params).options
      )
      .pipe(map(res => this.outputAdapter.postIdentificationStart(res)));
  }

  getIdentificationVerify(
    params: ApiParams<{ method: string }, void>
  ): Observable<IdentificationVerifyDTO> {
    const url = `${this.apiUrl}/v1/identifications/${params.pathParams.method}/verify`;

    return this.http
      .get<IdentificationVerifyDTO>(
        url,
        this.inputAdapter.getIdentificationVerify(params).options
      )
      .pipe(map(res => this.outputAdapter.getIdentificationVerify(res)));
  }

  getDigitalSignatureRequestDsp(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<DigitalSigningDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/digital-signature/request-dsp`;

    return this.http
      .get<DigitalSigningDTO>(
        url,
        this.inputAdapter.getDigitalSignatureRequestDsp(params).options
      )
      .pipe(map(res => this.outputAdapter.getDigitalSignatureRequestDsp(res)));
  }

  getDigitalSignatureRequestStatusDsp(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<DigitalSigningDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/digital-signature/request-status-dsp`;

    return this.http
      .get<DigitalSigningDTO>(
        url,
        this.inputAdapter.getDigitalSignatureRequestStatusDsp(params).options
      )
      .pipe(
        map(res => this.outputAdapter.getDigitalSignatureRequestStatusDsp(res))
      );
  }

  getDigitalSignatureSign(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<DigitalSigningDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/digital-signature/sign`;

    return this.http
      .get<DigitalSigningDTO>(
        url,
        this.inputAdapter.getDigitalSignatureSign(params).options
      )
      .pipe(map(res => this.outputAdapter.getDigitalSignatureSign(res)));
  }

  postPsd2Start(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<BankAccountLinkDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/psd2/start`;

    return this.http
      .post<BankAccountLinkDTO>(
        url,
        this.inputAdapter.postPsd2Start(params).body,
        this.inputAdapter.postPsd2Start(params).options
      )
      .pipe(map(res => this.outputAdapter.postPsd2Start(res)));
  }

  getPsd2Verify(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<BankAccountEvaluationDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/psd2/verify`;

    return this.http
      .get<BankAccountEvaluationDTO>(
        url,
        this.inputAdapter.getPsd2Verify(params).options
      )
      .pipe(map(res => this.outputAdapter.getPsd2Verify(res)));
  }

  getPositionQuotationBoundaries(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<QuotationBoundariesDTO> {
    const url = `${this.apiUrl}/v1/quotations/${params.pathParams.positionId}/boundaries`;

    return this.http
      .get<QuotationBoundariesDTO>(
        url,
        this.inputAdapter.getPositionQuotationBoundaries(params).options
      )
      .pipe(map(res => this.outputAdapter.getPositionQuotationBoundaries(res)));
  }

  putPositionQuotation(
    params: ApiParams<{ positionId: string }, QuotationParamsDTO>
  ): Observable<void> {
    const url = `${this.apiUrl}/v1/quotations/${params.pathParams.positionId}`;

    return this.http
      .put<void>(
        url,
        this.inputAdapter.putPositionQuotation(params).body,
        this.inputAdapter.putPositionQuotation(params).options
      )
      .pipe(map(res => this.outputAdapter.putPositionQuotation(res)));
  }

  putObligationConfirm(
    params: ApiParams<
      { positionId: string; obligationId: string },
      { isConfirmed: boolean }
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/obligation/${params.pathParams.positionId}/confirm/${params.pathParams.obligationId}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putObligationConfirm(params).body,
        this.inputAdapter.putObligationConfirm(params).options
      )
      .pipe(map(res => this.outputAdapter.putObligationConfirm(res)));
  }

  getNotes(
    params: ApiParams<{ positionId: string; type: string }, void>
  ): Observable<NoteDTO[]> {
    const url = `${this.apiUrl}/v1/note/${params.pathParams.positionId}/${params.pathParams.type}`;

    return this.http
      .get<NoteDTO[]>(url, this.inputAdapter.getNotes(params).options)
      .pipe(map(res => this.outputAdapter.getNotes(res)));
  }

  getAtc(params: ApiParams<void, void>): Observable<AtcDTO[]> {
    const url = `${this.apiUrl}/v1/atc`;

    return this.http
      .get<AtcDTO[]>(url, this.inputAdapter.getAtc(params).options)
      .pipe(map(res => this.outputAdapter.getAtc(res)));
  }

  postAtc(
    params: ApiParams<{ atcId: number; positionId: string }, AtcDTO>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/atc/${params.pathParams.atcId}/${params.pathParams.positionId}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postAtc(params).body,
        this.inputAdapter.postAtc(params).options
      )
      .pipe(map(res => this.outputAdapter.postAtc(res)));
  }

  postContactSendOtp(
    params: ApiParams<
      { positionId: string; channelId: string },
      { contact: string }
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/contacts/positions/${params.pathParams.positionId}/channel/${params.pathParams.channelId}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postContactSendOtp(params).body,
        this.inputAdapter.postContactSendOtp(params).options
      )
      .pipe(map(res => this.outputAdapter.postContactSendOtp(res)));
  }

  putContactVerifyOtp(
    params: ApiParams<
      { positionId: string; channelId: string },
      { contact: string; otp: string }
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/contacts/positions/${params.pathParams.positionId}/channel/${params.pathParams.channelId}`;

    return this.http
      .put<any>(
        url,
        this.inputAdapter.putContactVerifyOtp(params).body,
        this.inputAdapter.putContactVerifyOtp(params).options
      )
      .pipe(map(res => this.outputAdapter.putContactVerifyOtp(res)));
  }

  getNotifications(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<NotificationDTO[]> {
    const url = `${this.apiUrl}/v1/notifications/${params.pathParams.positionId}`;

    return this.http
      .get<NotificationDTO[]>(
        url,
        this.inputAdapter.getNotifications(params).options
      )
      .pipe(map(res => this.outputAdapter.getNotifications(res)));
  }

  putNotification(
    params: ApiParams<
      { notificationId: string; outcome: string },
      NotificationDTO
    >
  ): Observable<void> {
    const url = `${this.apiUrl}/v1/notifications/${params.pathParams.notificationId}/${params.pathParams.outcome}`;

    return this.http
      .put<void>(
        url,
        this.inputAdapter.putNotification(params).body,
        this.inputAdapter.putNotification(params).options
      )
      .pipe(map(res => this.outputAdapter.putNotification(res)));
  }

  getQuotationCreditRequest(
    params: ApiParams<{ mode: string }, void>
  ): Observable<CreditRequestDTO> {
    const url = `${this.apiUrl}/v1/quotations/calculate/${params.pathParams.mode}/creditrequest`;

    return this.http
      .get<CreditRequestDTO>(
        url,
        this.inputAdapter.getQuotationCreditRequest(params).options
      )
      .pipe(map(res => this.outputAdapter.getQuotationCreditRequest(res)));
  }

  getQuotationCreditUse(
    params: ApiParams<{ mode: string }, void>
  ): Observable<CreditUseListDTO> {
    const url = `${this.apiUrl}/v1/quotations/calculate/${params.pathParams.mode}/credituse`;

    return this.http
      .get<CreditUseListDTO>(
        url,
        this.inputAdapter.getQuotationCreditUse(params).options
      )
      .pipe(map(res => this.outputAdapter.getQuotationCreditUse(res)));
  }

  postPostselling(
    params: ApiParams<
      { positionId: string; taskCode: string },
      PostsellingCreateDTO
    >
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/postselling/${params.pathParams.positionId}/${params.pathParams.taskCode}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postPostselling(params).body,
        this.inputAdapter.postPostselling(params).options
      )
      .pipe(map(res => this.outputAdapter.postPostselling(res)));
  }

  postHandover(
    params: ApiParams<{ positionId: string; handoverCod: string }, void>
  ): Observable<HandoverStartDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/handover/${params.pathParams.handoverCod}`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postHandover(params).body,
        this.inputAdapter.postHandover(params).options
      )
      .pipe(map(res => this.outputAdapter.postHandover(res)));
  }

  getHandover(
    params: ApiParams<{ positionId: string; handoverCod: string }, void>
  ): Observable<HandoverStatusDTO> {
    const url = `${this.apiUrl}/v1/position/${params.pathParams.positionId}/handover/${params.pathParams.handoverCod}`;

    return this.http
      .get<any>(url, this.inputAdapter.getHandover(params).options)
      .pipe(map(res => this.outputAdapter.getHandover(res)));
  }

  getChannel(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<ChannelDTO> {
    const url = `${this.baseUrl}/v1/chains/${params.pathParams.positionId}`;

    return this.http
      .get<ChannelDTO>(url, this.inputAdapter.getChannel(params).options)
      .pipe(map(res => this.outputAdapter.getChannel(params)));
  }
}
